import type {
  DisplayGroupNode,
  StateMachineEdge,
  StateMachineNode,
  StateMachineState
} from '../ProgramStateMachine.types'

const NODE_VERTICAL_SPACING = 200
const INTRA_GROUP_VERTICAL_SPACING = 100
const NODE_HORIZONTAL_SPACING = 300
const NODE_MIN_WIDTH = 120
const NODE_MAX_WIDTH = 160
const NODE_NON_LABEL_WIDTH = 38 // padding (10+10) + chevron (8+8) + border (2)
const CHAR_WIDTH = 8 // approximate at font-size 13px
const EDGE_CLEARANCE = 40
export const GROUP_PADDING = 16
export const GROUP_LABEL_HEIGHT = 24
export const NODE_HEIGHT_ESTIMATE = 48
const HALF_H = NODE_HEIGHT_ESTIMATE / 2

/** Estimate rendered width of a node from its label text */
export const estimateNodeWidth = (label: string): number =>
  Math.min(NODE_MAX_WIDTH, Math.max(NODE_MIN_WIDTH, label.length * CHAR_WIDTH + NODE_NON_LABEL_WIDTH))

interface LayoutResult {
  nodes: StateMachineNode[]
  edges: StateMachineEdge[]
  groupNodes: DisplayGroupNode[]
}

export const calculateLayout = (states: StateMachineState[]): LayoutResult => {
  if (states.length === 0) return { nodes: [], edges: [], groupNodes: [] }

  // Estimate per-node widths from label text
  const nodeWidths = new Map<string, number>()
  for (const state of states) {
    const label = state.metadata?.displayKey || state.id
    nodeWidths.set(state.id, estimateNodeWidth(label))
  }
  const halfW = (id: string) => (nodeWidths.get(id) ?? NODE_MIN_WIDTH) / 2

  // Track cycles
  const cycles = new Set<string>()
  const visited = new Set<string>()
  const stack = new Set<string>()

  const findCycles = (stateId: string): void => {
    visited.add(stateId)
    stack.add(stateId)

    const state = states.find(s => s.id === stateId)

    if (state) {
      for (const transition of state.transitions) {
        if (stack.has(transition.to)) {
          cycles.add(`${stateId}-${transition.to}`)
        } else if (!visited.has(transition.to)) {
          findCycles(transition.to)
        }
      }
    }

    stack.delete(stateId)
  }

  for (const state of states) {
    if (!visited.has(state.id)) {
      findCycles(state.id)
    }
  }

  // Compute entry point: first state with no incoming edges (excluding cyclic)
  const hasIncomingEdges = new Set(
    states.flatMap(state => state.transitions.filter(t => !cycles.has(`${state.id}-${t.to}`)).map(t => t.to))
  )
  const entryPointId = states.find(s => !hasIncomingEdges.has(s.id))?.id

  // Build forward adjacency (excluding cyclic edges) and parent map
  const stateIds = new Set(states.map(s => s.id))
  const children = new Map<string, string[]>()
  const parents = new Map<string, string[]>()

  for (const state of states) {
    if (!children.has(state.id)) children.set(state.id, [])

    if (!parents.has(state.id)) parents.set(state.id, [])

    for (const t of state.transitions) {
      if (!cycles.has(`${state.id}-${t.to}`) && stateIds.has(t.to)) {
        children.get(state.id)!.push(t.to)

        if (!parents.has(t.to)) parents.set(t.to, [])
        parents.get(t.to)!.push(state.id)
      }
    }
  }

  // Assign layers using longest-path from roots (ensures merge nodes wait for all parents)
  const layers = new Map<string, number>()

  const roots = states.filter(s => (parents.get(s.id) ?? []).length === 0)

  if (roots.length === 0 && states.length > 0) {
    roots.push(states[0])
  }

  for (const root of roots) {
    layers.set(root.id, 0)
  }

  // Iterative longest-path: process each node, propagating max layer to children
  const queue = [...roots.map(r => r.id)]
  while (queue.length > 0) {
    const current = queue.shift()!
    const currentLayer = layers.get(current)!

    for (const child of children.get(current) ?? []) {
      const newLayer = currentLayer + 1

      if (!layers.has(child) || layers.get(child)! < newLayer) {
        layers.set(child, newLayer)
        queue.push(child)
      }
    }
  }

  // Place any unreachable states at the bottom
  let maxLayer = 0

  for (const [, layer] of layers) {
    if (layer > maxLayer) maxLayer = layer
  }

  for (const state of states) {
    if (!layers.has(state.id)) {
      maxLayer += 1
      layers.set(state.id, maxLayer)
    }
  }

  // Group states by layer, ordered by BFS discovery (respects transition order)
  const layerGroups = new Map<number, string[]>()

  for (const state of states) {
    const layer = layers.get(state.id)!

    if (!layerGroups.has(layer)) layerGroups.set(layer, [])
    layerGroups.get(layer)!.push(state.id)
  }

  // Build displayGroup lookup for intra-group spacing decisions
  const stateGroupLookup = new Map<string, string>()

  for (const state of states) {
    if (state.metadata?.displayGroup) {
      stateGroupLookup.set(state.id, state.metadata.displayGroup)
    }
  }

  // Determine which consecutive layer pairs share a group (use tight spacing)
  const sortedLayers = [...layerGroups.keys()].sort((a, b) => a - b)
  const intraGroupLayerPairs = new Set<number>()

  for (let li = 1; li < sortedLayers.length; li += 1) {
    const prevLayer = sortedLayers[li - 1]
    const currLayer = sortedLayers[li]
    const prevNodes = layerGroups.get(prevLayer)!
    const currNodes = layerGroups.get(currLayer)!

    // Check if any group has members on both layers
    const prevGroups = new Set(prevNodes.map(id => stateGroupLookup.get(id)).filter(Boolean))

    for (const id of currNodes) {
      const g = stateGroupLookup.get(id)

      if (g && prevGroups.has(g)) {
        intraGroupLayerPairs.add(currLayer)
        break
      }
    }
  }

  // Compute cumulative y offsets per layer using tight spacing for intra-group transitions
  // Positions are stored as center coordinates; HALF_H offset ensures top-left starts at y=0
  const layerY = new Map<number, number>()
  layerY.set(sortedLayers[0], HALF_H)

  for (let li = 1; li < sortedLayers.length; li += 1) {
    const currLayer = sortedLayers[li]
    const prevY = layerY.get(sortedLayers[li - 1])!
    const spacing = intraGroupLayerPairs.has(currLayer) ? INTRA_GROUP_VERTICAL_SPACING : NODE_VERTICAL_SPACING

    layerY.set(currLayer, prevY + spacing)
  }

  // Order nodes within each layer by average parent x-position for minimal crossings
  // Positions are stored as CENTER coordinates (cx, cy) of each node
  const positions = new Map<string, { x: number; y: number }>()

  for (const layer of sortedLayers) {
    const nodesInLayer = layerGroups.get(layer)!
    const layerYVal = layerY.get(layer)!

    // Place each node at its parent(s) center
    for (const id of nodesInLayer) {
      const nodeParents = (parents.get(id) ?? []).filter(p => positions.has(p))
      positions.set(id, {
        x:
          nodeParents.length > 0
            ? nodeParents.reduce((s, p) => s + positions.get(p)!.x, 0) / nodeParents.length
            : halfW(id),
        y: layerYVal
      })
    }

    // Sort by x so we can detect overlaps left-to-right
    nodesInLayer.sort((a, b) => positions.get(a)!.x - positions.get(b)!.x)

    // Spread overlapping nodes apart in passes until settled.
    // Each pass finds runs of nodes that are too close and spreads them
    // evenly around their midpoint, preserving center of mass.
    for (let pass = 0; pass < nodesInLayer.length; pass += 1) {
      let changed = false
      let i = 0

      while (i < nodesInLayer.length) {
        // Find a maximal run of overlapping nodes starting at i
        let j = i + 1

        while (
          j < nodesInLayer.length &&
          positions.get(nodesInLayer[j])!.x - positions.get(nodesInLayer[j - 1])!.x < NODE_HORIZONTAL_SPACING
        ) {
          j += 1
        }

        if (j > i + 1) {
          // Spread this run evenly, centered on its current midpoint
          const first = positions.get(nodesInLayer[i])!.x
          const last = positions.get(nodesInLayer[j - 1])!.x
          const mid = (first + last) / 2
          const count = j - i
          const startX = mid - ((count - 1) * NODE_HORIZONTAL_SPACING) / 2

          for (let k = i; k < j; k += 1) {
            positions.get(nodesInLayer[k])!.x = startX + (k - i) * NODE_HORIZONTAL_SPACING
          }

          changed = true
        }

        i = j
      }

      if (!changed) break
    }
  }

  // --- Group processing (before edge routing so overlap shifts are reflected in routing) ---
  // Collect groups: Map<groupName, stateId[]>
  const groupMap = new Map<string, string[]>()

  for (const state of states) {
    const groupName = state.metadata?.displayGroup

    if (groupName) {
      if (!groupMap.has(groupName)) groupMap.set(groupName, [])
      groupMap.get(groupName)!.push(state.id)
    }
  }

  // Contiguity splitting & bounding box computation
  interface GroupSegment {
    groupNodeId: string
    groupName: string
    segmentIndex?: number
    memberIds: string[]
    x: number
    y: number
    width: number
    height: number
  }

  const groupSegments: GroupSegment[] = []
  const memberToGroupNode = new Map<string, string>()

  for (const [groupName, memberIds] of groupMap) {
    // Sort members by layer
    const sorted = [...memberIds].sort((a, b) => (layers.get(a) ?? 0) - (layers.get(b) ?? 0))

    // Split into contiguous segments by layer gaps
    const segments: string[][] = []

    if (sorted.length > 0) {
      let currentSegment: string[] = [sorted[0]]
      const allLayerValues = new Set(sorted.map(id => layers.get(id)!))

      for (let i = 1; i < sorted.length; i += 1) {
        const prevLayer = layers.get(sorted[i - 1])!
        const currLayer = layers.get(sorted[i])!

        // Check for gap: any layer between prev and curr that has no group member
        let hasGap = false

        for (let l = prevLayer + 1; l < currLayer; l += 1) {
          if (!allLayerValues.has(l)) {
            hasGap = true
            break
          }
        }

        if (hasGap) {
          segments.push(currentSegment)
          currentSegment = [sorted[i]]
        } else {
          currentSegment.push(sorted[i])
        }
      }
      segments.push(currentSegment)
    }

    segments.forEach((segment, segIdx) => {
      const groupNodeId = segments.length > 1 ? `__group_${groupName}_${segIdx}__` : `__group_${groupName}__`

      // Positions are centers — expand by per-node half-widths to get extents
      const memberPositions = segment.map(id => ({ id, ...positions.get(id)! }))
      const leftEdges = memberPositions.map(p => p.x - halfW(p.id))
      const rightEdges = memberPositions.map(p => p.x + halfW(p.id))
      const minLeft = Math.min(...leftEdges)
      const maxRight = Math.max(...rightEdges)
      const minCY = Math.min(...memberPositions.map(p => p.y))
      const maxCY = Math.max(...memberPositions.map(p => p.y))

      const gx = minLeft - GROUP_PADDING
      const gy = minCY - HALF_H - GROUP_PADDING - GROUP_LABEL_HEIGHT
      const gw = maxRight - minLeft + GROUP_PADDING * 2
      const gh = maxCY - minCY + NODE_HEIGHT_ESTIMATE + GROUP_PADDING * 2 + GROUP_LABEL_HEIGHT

      groupSegments.push({
        groupNodeId,
        groupName,
        segmentIndex: segments.length > 1 ? segIdx : undefined,
        memberIds: segment,
        x: gx,
        y: gy,
        width: gw,
        height: gh
      })

      for (const memberId of segment) {
        memberToGroupNode.set(memberId, groupNodeId)
      }
    })
  }

  // Resolve group-vs-group horizontal overlaps by shifting both groups outward equally
  // Sort segments by x position so we process left-to-right
  groupSegments.sort((a, b) => a.x - b.x)

  const computeGroupBounds = (memberIds: string[]) => {
    const memberPositions = memberIds.map(id => ({ id, ...positions.get(id)! }))
    const leftEdges = memberPositions.map(p => p.x - halfW(p.id))
    const rightEdges = memberPositions.map(p => p.x + halfW(p.id))
    const minLeft = Math.min(...leftEdges)
    const maxRight = Math.max(...rightEdges)
    const minCY = Math.min(...memberPositions.map(p => p.y))
    const maxCY = Math.max(...memberPositions.map(p => p.y))

    return {
      x: minLeft - GROUP_PADDING,
      y: minCY - HALF_H - GROUP_PADDING - GROUP_LABEL_HEIGHT,
      width: maxRight - minLeft + GROUP_PADDING * 2,
      height: maxCY - minCY + NODE_HEIGHT_ESTIMATE + GROUP_PADDING * 2 + GROUP_LABEL_HEIGHT
    }
  }

  for (let i = 0; i < groupSegments.length; i += 1) {
    for (let j = i + 1; j < groupSegments.length; j += 1) {
      const a = groupSegments[i]
      const b = groupSegments[j]

      const aRight = a.x + a.width
      const aBottom = a.y + a.height
      const bRight = b.x + b.width
      const bBottom = b.y + b.height

      // Check overlap (both axes must overlap)
      if (aRight > b.x && a.x < bRight && aBottom > b.y && a.y < bBottom) {
        const overlap = aRight + GROUP_PADDING - b.x
        const shiftA = -overlap / 2 // move a left
        const shiftB = overlap / 2 // move b right

        for (const memberId of a.memberIds) {
          const pos = positions.get(memberId)

          if (pos) pos.x += shiftA
        }

        for (const memberId of b.memberIds) {
          const pos = positions.get(memberId)

          if (pos) pos.x += shiftB
        }

        Object.assign(a, computeGroupBounds(a.memberIds))
        Object.assign(b, computeGroupBounds(b.memberIds))
      }
    }
  }

  // Shift ungrouped nodes that overlap with group bounding boxes
  const groupedNodeIds = new Set(memberToGroupNode.keys())

  for (const state of states) {
    if (!groupedNodeIds.has(state.id)) {
      const pos = positions.get(state.id)

      if (pos) {
        const hw = halfW(state.id)
        for (const seg of groupSegments) {
          const nodeLeft = pos.x - hw
          const nodeRight = pos.x + hw
          const nodeTop = pos.y - HALF_H
          const nodeBottom = pos.y + HALF_H
          const groupRight = seg.x + seg.width
          const groupBottom = seg.y + seg.height
          const groupCenterX = seg.x + seg.width / 2

          // Check if node overlaps with group bounding box
          if (nodeRight > seg.x && nodeLeft < groupRight && nodeBottom > seg.y && nodeTop < groupBottom) {
            if (pos.x < groupCenterX) {
              pos.x = seg.x - GROUP_PADDING - hw
            } else {
              pos.x = groupRight + GROUP_PADDING + hw
            }
          }
        }
      }
    }
  }

  // Collect edge descriptors grouped by source and target for handle assignment
  const edgeDescriptors = states.flatMap(state =>
    state.transitions.map((transition, index) => ({
      id: `${state.id}-${transition.to}-${index}`,
      source: state.id,
      target: transition.to,
      transition,
      isCyclic: cycles.has(`${state.id}-${transition.to}`)
    }))
  )

  const descriptorsBySource = new Map<string, typeof edgeDescriptors>()
  const descriptorsByTarget = new Map<string, typeof edgeDescriptors>()

  for (const desc of edgeDescriptors) {
    if (!descriptorsBySource.has(desc.source)) descriptorsBySource.set(desc.source, [])
    descriptorsBySource.get(desc.source)!.push(desc)

    if (!descriptorsByTarget.has(desc.target)) descriptorsByTarget.set(desc.target, [])
    descriptorsByTarget.get(desc.target)!.push(desc)
  }

  // Compute edge routing BEFORE handle assignment so handles reflect arrival direction
  // Prefer 2-corner L-paths; fall back to 4-corner S-shape detour only when needed
  const crossModeMap = new Map<string, 'top' | 'bottom'>()
  const detourMap = new Map<string, number>()

  const isVerticalClearAt = (x: number, nodes: StateMachineState[]) =>
    nodes.every(s => {
      const nodePos = positions.get(s.id)!
      const hw = halfW(s.id)

      return x < nodePos.x - hw - EDGE_CLEARANCE || x > nodePos.x + hw + EDGE_CLEARANCE
    })

  for (const desc of edgeDescriptors) {
    if (!desc.isCyclic) {
      const sourceLayer = layers.get(desc.source)!
      const targetLayer = layers.get(desc.target)!

      if (targetLayer - sourceLayer > 1) {
        const sourcePos = positions.get(desc.source)!
        const targetPos = positions.get(desc.target)!
        const sourceHandleX = sourcePos.x
        const targetHandleX = targetPos.x

        const intermediateNodes = states.filter(s => {
          const l = layers.get(s.id)

          return l !== undefined && l > sourceLayer && l < targetLayer
        })

        if (intermediateNodes.length > 0) {
          const corridorLeft = Math.min(sourceHandleX, targetHandleX)
          const corridorRight = Math.max(sourceHandleX, targetHandleX)

          const isBlocked = intermediateNodes.some(s => {
            const nodePos = positions.get(s.id)!
            const hw = halfW(s.id)

            return nodePos.x + hw + EDGE_CLEARANCE > corridorLeft && nodePos.x - hw - EDGE_CLEARANCE < corridorRight
          })

          if (isBlocked) {
            // Try 2-corner L-paths first: vertical at targetX or sourceX
            if (isVerticalClearAt(targetHandleX, intermediateNodes)) {
              crossModeMap.set(desc.id, 'top')
            } else if (isVerticalClearAt(sourceHandleX, intermediateNodes)) {
              crossModeMap.set(desc.id, 'bottom')
            } else {
              // Neither endpoint clears — fall back to S-shape detour
              const intermediateExtents = intermediateNodes.map(s => {
                const np = positions.get(s.id)!
                const hw = halfW(s.id)

                return { left: np.x - hw, right: np.x + hw }
              })

              const clearLeft = Math.min(...intermediateExtents.map(e => e.left)) - EDGE_CLEARANCE
              const clearRight = Math.max(...intermediateExtents.map(e => e.right)) + EDGE_CLEARANCE

              const blockingCenterX =
                intermediateNodes.reduce((sum, s) => sum + positions.get(s.id)!.x, 0) / intermediateNodes.length
              const detourX = targetHandleX >= blockingCenterX ? clearRight : clearLeft

              detourMap.set(desc.id, detourX)
            }
          }
        }
      }
    }
  }

  // Assign source handles: all outgoing edges share a single centered handle
  const sourceHandleMap = new Map<string, string>()

  for (const [sourceId, descs] of descriptorsBySource) {
    for (const desc of descs) {
      sourceHandleMap.set(desc.id, `${sourceId}-source-0`)
    }
  }

  // Assign target handles: sort incoming edges by effective arrival direction
  // For detoured edges, use detourX as the effective source direction
  const targetHandleMap = new Map<string, string>()

  for (const [targetId, descs] of descriptorsByTarget) {
    descs.sort((a, b) => {
      const aX = detourMap.get(a.id) ?? positions.get(a.source)?.x ?? 0
      const bX = detourMap.get(b.id) ?? positions.get(b.source)?.x ?? 0

      return aX - bX
    })
    descs.forEach((desc, idx) => {
      targetHandleMap.set(desc.id, `${targetId}-target-${idx}`)
    })
  }

  // Build group nodes (must appear before their children in the array)
  const groupNodes: DisplayGroupNode[] = groupSegments.map(seg => ({
    id: seg.groupNodeId,
    type: 'displayGroupNode',
    position: { x: seg.x, y: seg.y },
    style: { width: seg.width, height: seg.height },
    selectable: true,
    draggable: false,
    data: {
      label: seg.groupName,
      groupName: seg.groupName,
      ...(seg.segmentIndex !== undefined ? { segmentIndex: seg.segmentIndex } : {})
    }
  }))

  // Build state nodes — convert center coords to top-left, then make relative to group if grouped
  const stateNodes: StateMachineNode[] = states.map(state => {
    const hw = halfW(state.id)
    const center = positions.get(state.id) ?? { x: hw, y: HALF_H }
    const absPos = { x: center.x - hw, y: center.y - HALF_H }
    const parentGroupId = memberToGroupNode.get(state.id)
    const groupSeg = parentGroupId ? groupSegments.find(s => s.groupNodeId === parentGroupId) : undefined

    const position = groupSeg ? { x: absPos.x - groupSeg.x, y: absPos.y - groupSeg.y } : absPos

    return {
      id: state.id,
      type: 'stateMachineNode',
      position,
      ...(parentGroupId ? { parentId: parentGroupId } : {}),
      data: {
        label: state.metadata?.displayKey || state.id,
        stateId: state.id,
        transitions: state.transitions,
        isTerminal: state.isTerminal ?? state.transitions.length === 0,
        isEntryPoint: state.id === entryPointId,
        sourceHandleCount: (descriptorsBySource.get(state.id) ?? []).length,
        targetHandleCount: (descriptorsByTarget.get(state.id) ?? []).length,
        estimatedWidth: nodeWidths.get(state.id) ?? NODE_MIN_WIDTH,
        metadata: state.metadata,
        sideEffects: state.sideEffects
      }
    }
  })

  // Order state nodes: ungrouped first, then grouped (per group node, members after their group)
  // The visualiser will interleave groupNodes before their children (xyflow requirement)
  const ungroupedStateNodes = stateNodes.filter(n => !groupedNodeIds.has(n.id))
  const orderedStateNodes: StateMachineNode[] = [...ungroupedStateNodes]

  for (const gNode of groupNodes) {
    const seg = groupSegments.find(s => s.groupNodeId === gNode.id)

    if (seg) {
      for (const memberId of seg.memberIds) {
        const memberNode = stateNodes.find(n => n.id === memberId)

        if (memberNode) {
          orderedStateNodes.push(memberNode)
        }
      }
    }
  }

  const edges: StateMachineEdge[] = edgeDescriptors.map(desc => ({
    id: desc.id,
    source: desc.source,
    target: desc.target,
    sourceHandle: sourceHandleMap.get(desc.id)!,
    targetHandle: targetHandleMap.get(desc.id)!,
    type: 'stateMachineEdge' as const,
    data: {
      eventType: desc.transition.when?.eventsMatching?.events?.[0]?.type,
      payload: {
        conditions: desc.transition.when?.eventsMatching?.events?.[0]?.payload?.conditions
      },
      ...(crossModeMap.has(desc.id) ? { crossMode: crossModeMap.get(desc.id) } : {}),
      ...(detourMap.has(desc.id) ? { detourX: detourMap.get(desc.id) } : {})
    },
    style: desc.isCyclic
      ? {
          stroke: '#ff6b6b',
          strokeWidth: 2,
          strokeDasharray: '5,5'
        }
      : undefined
  }))

  return { nodes: orderedStateNodes, edges, groupNodes }
}
